/**
 * Font Picker Component
 *
 * A searchable, paginated font picker with font-rendered previews.
 * Uses Local Font Access API for system fonts when available.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronLeft, ChevronRight, Monitor, X } from 'lucide-react';
import { useLocalFonts, usePaginatedFonts } from '@/hooks/useLocalFonts';
import { parseFontStack, buildFontStack, getGenericFallback } from '@/types/fonts';

interface FontPickerProps {
  value: string; // CSS font-family string with fallbacks
  onChange: (fontFamily: string) => void;
  className?: string;
}

export function FontPicker({ value, onChange, className = '' }: FontPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { fonts, isLoading, isSystemFontsSupported, hasPermission, requestPermission } =
    useLocalFonts();

  const {
    fonts: paginatedFonts,
    totalFonts,
    page,
    totalPages,
    searchQuery,
    setSearchQuery,
    hasNextPage,
    hasPrevPage,
    nextPage,
    prevPage,
  } = usePaginatedFonts(fonts, 30);

  // Parse current value
  const { primary } = parseFontStack(value);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false);
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleSelect = (fontFamily: string) => {
    const fallback = getGenericFallback(fontFamily);
    const stack = buildFontStack(fontFamily, [fallback]);
    onChange(stack);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-navy border border-white/10 rounded-lg px-3 py-1.5 text-sm text-fg text-left hover:border-white/20 focus:outline-none focus:border-primary/50 transition-colors"
        style={{ fontFamily: value }}
      >
        <span className="block truncate">{primary}</span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-72 bg-navy-light border border-white/10 rounded-lg shadow-xl overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-white/10">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search fonts..."
                className="w-full bg-navy border border-white/10 rounded-md pl-8 pr-8 py-1.5 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:border-primary/50"
                autoFocus
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-muted hover:text-fg"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* System fonts button */}
          {isSystemFontsSupported && !hasPermission && (
            <div className="p-2 border-b border-white/10">
              <button
                onClick={requestPermission}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary/10 hover:bg-primary/20 text-primary text-sm rounded-md transition-colors disabled:opacity-50"
              >
                <Monitor size={14} />
                {isLoading ? 'Loading...' : 'Load System Fonts'}
              </button>
            </div>
          )}

          {/* Font list */}
          <div className="max-h-64 overflow-y-auto">
            {paginatedFonts.map((font) => (
              <button
                key={font.family}
                onClick={() => handleSelect(font.family)}
                className={`
                  w-full px-3 py-2 text-left hover:bg-white/5 transition-colors
                  flex items-center justify-between
                  ${font.family === primary ? 'bg-primary/10 text-primary' : 'text-fg'}
                `}
              >
                <span
                  className="text-sm truncate"
                  style={{ fontFamily: `"${font.family}", ${getGenericFallback(font.family)}` }}
                >
                  {font.displayName}
                </span>
                {font.source === 'system' && (
                  <span className="text-[10px] text-fg-muted uppercase tracking-wider ml-2 flex-shrink-0">
                    System
                  </span>
                )}
              </button>
            ))}

            {paginatedFonts.length === 0 && (
              <div className="px-3 py-8 text-center text-fg-muted text-sm">
                No fonts found
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-2 border-t border-white/10 flex items-center justify-between">
              <button
                onClick={prevPage}
                disabled={!hasPrevPage}
                className="p-1 text-fg-muted hover:text-fg disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-xs text-fg-muted">
                {page + 1} / {totalPages} ({totalFonts} fonts)
              </span>
              <button
                onClick={nextPage}
                disabled={!hasNextPage}
                className="p-1 text-fg-muted hover:text-fg disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
